export const guitarSyllabus = {
  1: {
    id: 1,
    title: "Level 1 — Initial Basic (Foundation Level)",
    objective:
      "To establish instrument familiarity, rhythmic awareness, pitch recognition and basic musical understanding.",
    practicalSections: [
      {
        title: "Instrument fundamentals",
        items: [
          "Parts of the guitar (acoustic / electric)",
          "Correct posture (sitting and standing)",
        ],
      },
      {
        title: "Right-hand techniques",
        items: ["Basic strumming", "Thumb–index alternation"],
      },
      {
        title: "Left-hand techniques",
        items: ["Finger numbering", "Basic fretting accuracy"],
      },
      {
        title: "Notes and playing",
        items: [
          "Open string exercises",
          "First-position notes (up to 3rd fret)",
          "Simple melodic phrases",
        ],
      },
      {
        title: "Scales",
        items: [
          "C Major",
          "G Major",
          "D Major",
          "A Minor (Natural)",
          "E Minor (Natural)",
          "A Major (one octave, first position)",
        ],
      },
      {
        title: "Chords",
        items: [
          "Open major chords: C, G, D, A, E",
          "Open minor chords: Am, Em, Dm",
          "Simple chord transitions",
        ],
      },
      {
        title: "Rhythm and strumming",
        items: [
          "Whole, half, quarter notes",
          "Simple down-stroke strumming patterns",
          "4/4 and 3/4 time",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Two pieces in tabs and chords (one learner’s choice, one technical)",
          "Optional self-composed melody (8–16 bars)",
        ],
      },
    ],
    theorySections: [
      {
        title: "Music fundamentals",
        items: [
          "What is music; sound vs silence",
          "Pitch and duration",
          "Musical alphabet (A–G)",
          "Whole, half and quarter notes and rests",
          "Intervals: whole step / half step (tone / semitone)",
        ],
      },
      {
        title: "Scales and chords",
        items: [
          "Concept of major and minor scales",
          "Structure of the major scale (W–W–H–W–W–W–H)",
          "Basic concept of major vs minor chord",
        ],
      },
      {
        title: "Notation (optional)",
        items: [
          "Staff basics or tablature reading",
          "Time signatures (2/4, 3/4, 4/4)",
        ],
      },
    ],
    questionBank: {
      title: "Sample theory question bank – Level 1",
      sections: [
        {
          label: "Section A: Multiple Choice",
          items: [
            "How many strings does a standard guitar have?",
            "Which note comes after G?",
            "What is a semitone?",
          ],
        },
        {
          label: "Section B: Short Answer",
          items: [
            "Define rhythm.",
            "Name two open strings on the guitar.",
            "What is a major chord?",
          ],
        },
        {
          label: "Section C: Applied",
          items: [
            "Write the notes of the C major scale.",
            "Identify whether a given chord is major or minor (audio or diagram).",
          ],
        },
      ],
    },
  },
  2: {
    id: 2,
    title: "Level 2 — Intermediate (Developing Control)",
    objective:
      "To develop fluency, coordination, rhythmic precision and theoretical clarity.",
    practicalSections: [
      {
        title: "Technique",
        items: [
          "Alternate picking",
          "Basic fingerstyle patterns",
          "Hammer-ons and pull-offs",
          "Clean chord transitions",
        ],
      },
      {
        title: "Scales",
        items: [
          "E Major",
          "B Minor (Natural)",
          "F Major",
          "D Minor (Natural)",
          "B♭ Major",
          "C Minor (Natural) (two octaves where possible)",
        ],
      },
      {
        title: "Chords",
        items: [
          "Introduction to barre chords (E and A shapes)",
          "Power chords",
          "Dominant 7th chords",
        ],
      },
      {
        title: "Rhythm",
        items: [
          "Eighth notes and rests",
          "Basic syncopation",
          "Use of metronome in practice",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Three pieces in tabs and chords (contrasting styles)",
          "At least one piece including barre chords",
          "Simple improvisation over a basic progression",
        ],
      },
    ],
    theorySections: [
      {
        title: "Rhythm and notation",
        items: ["Eighth notes and rests", "Simple syncopation"],
      },
      {
        title: "Scales and keys",
        items: [
          "Relative major and minor",
          "Key signatures up to three sharps / flats",
        ],
      },
      {
        title: "Intervals and chords",
        items: [
          "Major, minor and perfect intervals",
          "Construction of major, minor, diminished and augmented triads",
        ],
      },
      {
        title: "Harmony",
        items: ["I–IV–V progressions", "Cadence basics"],
      },
    ],
    questionBank: {
      title: "Sample theory question bank – Level 2",
      sections: [
        {
          label: "Section A",
          items: [
            "What is a key signature?",
            "What is a relative minor?",
          ],
        },
        {
          label: "Section B",
          items: [
            "Explain whole step vs half step.",
            "Write an I–IV–V progression in G major.",
          ],
        },
        {
          label: "Section C",
          items: [
            "Identify intervals from given notes.",
            "Construct a minor triad.",
          ],
        },
      ],
    },
  },
  3: {
    id: 3,
    title: "Level 3 — Advanced 1 (Musical Expansion)",
    objective:
      "To build musical expression, technical strength and harmonic understanding.",
    practicalSections: [
      {
        title: "Technique",
        items: [
          "Palm muting",
          "Slides, bends and vibrato",
          "Fingerstyle independence",
          "Scale sequencing",
        ],
      },
      {
        title: "Scales",
        items: [
          "A Major",
          "E Minor (Harmonic)",
          "D Major",
          "F♯ Minor (Natural)",
          "G Minor",
          "E♭ Major",
        ],
      },
      {
        title: "Chords",
        items: [
          "Extended chords (maj7, min7, dom7)",
          "Movable chord voicings",
          "Arpeggiated chords",
        ],
      },
      {
        title: "Rhythm",
        items: ["Triplets", "6/8 and other compound time signatures"],
      },
      {
        title: "Repertoire",
        items: [
          "Three contrasting pieces in tabs and chords (one self-arranged or self-composed)",
          "Assessment of stylistic interpretation",
        ],
      },
    ],
    theorySections: [
      {
        title: "Scales and functions",
        items: [
          "Harmonic minor scales",
          "Scale degrees and functional roles",
        ],
      },
      {
        title: "Chords and harmony",
        items: [
          "Seventh-chord construction",
          "Diatonic harmony in major and minor keys",
        ],
      },
      {
        title: "Rhythm and form",
        items: [
          "Compound meters",
          "Awareness of simple polyrhythms",
          "Binary, ternary and verse–chorus forms",
        ],
      },
    ],
    questionBank: {
      title: "Sample theory question bank – Level 3",
      sections: [
        {
          label: "Section A",
          items: [
            "What is a harmonic minor scale?",
            "Define compound time.",
          ],
        },
        {
          label: "Section B",
          items: [
            "Build a dominant 7th chord.",
            "Explain chord function.",
          ],
        },
        {
          label: "Section C",
          items: [
            "Analyze a simple chord progression.",
            "Identify the form of a given piece.",
          ],
        },
      ],
    },
  },
  4: {
    id: 4,
    title: "Level 4 — Advanced 2 (Artistic Control)",
    objective:
      "To achieve interpretive maturity and integrated technical–theoretical control.",
    practicalSections: [
      {
        title: "Technique",
        items: [
          "Advanced fingerstyle patterns",
          "Optional tapping work",
          "Secure position shifts across the neck",
        ],
      },
      {
        title: "Scales",
        items: [
          "C♯ Minor",
          "F Major (two octaves)",
          "A♭ Major",
          "D Harmonic Minor",
          "G Major in multiple positions",
          "B♭ Minor",
        ],
      },
      {
        title: "Chords",
        items: [
          "Altered chords",
          "Suspended chords",
          "Drop voicings and spread voicings",
        ],
      },
      {
        title: "Repertoire and improvisation",
        items: [
          "Four pieces in tabs and chords spanning at least two genres",
          "One long-form piece (extended structure)",
          "Improvisation over more complex chord changes",
        ],
      },
    ],
    theorySections: [
      {
        title: "Harmony and modes",
        items: [
          "Secondary dominants",
          "Modal interchange",
          "Introduction to modes (Ionian to Locrian)",
        ],
      },
      {
        title: "Analysis",
        items: [
          "Harmonic analysis of selected repertoire",
        ],
      },
    ],
    questionBank: {
      title: "Sample theory question bank – Level 4",
      sections: [
        {
          label: "Section A",
          items: [
            "What is modal interchange?",
            "Define secondary dominants.",
          ],
        },
        {
          label: "Section B",
          items: [
            "Explain the difference between modes.",
            "Write chord extensions for a given scale.",
          ],
        },
        {
          label: "Section C",
          items: [
            "Harmonic analysis of a short excerpt.",
            "Mode identification from melody.",
          ],
        },
      ],
    },
  },
  5: {
    id: 5,
    title: "Level 5 — Professional",
    objective:
      "To demonstrate professional-level performance, stylistic command and theoretical mastery.",
    practicalSections: [
      {
        title: "Technique",
        items: [
          "Agreed speed and accuracy benchmarks",
          "Advanced articulation and tone control",
          "Stylistic authenticity across chosen genres",
        ],
      },
      {
        title: "Scales and vocabulary",
        items: [
          "All major and natural minor scales across the neck",
          "Harmonic and melodic minor scales",
          "Pentatonic (major and minor) and blues scales",
        ],
      },
      {
        title: "Repertoire and improvisation",
        items: [
          "Five pieces in tabs and chords, including one original composition",
          "At least one technically demanding work",
          "Live improvisation over extended progressions",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced harmony and rhythm",
        items: [
          "Chord substitutions and reharmonisation",
          "Modulation",
          "Voice leading",
          "Odd meters (5/4, 7/8) and polyrhythms",
        ],
      },
      {
        title: "Composition and notation",
        items: [
          "Writing for guitar",
          "Lead sheet creation",
        ],
      },
    ],
    questionBank: {
      title: "Sample theory question bank – Level 5",
      sections: [
        {
          label: "Section A",
          items: [
            "What is modulation?",
            "Explain voice leading.",
          ],
        },
        {
          label: "Section B",
          items: [
            "Describe chord substitution techniques.",
            "Write a lead sheet.",
          ],
        },
        {
          label: "Section C",
          items: [
            "Analyze a professional-level piece.",
            "Compose an 8-bar harmonic progression.",
          ],
        },
      ],
    },
  },
  6: {
    id: 6,
    title: "Level 6 — Mastery (Artist / Educator / Performer Level)",
    objective:
      "To certify artistic performance authority, originality and pedagogical understanding.",
    practicalSections: [
      {
        title: "Recital requirements",
        items: [
          "A full recital of approximately 30–45 minutes",
          "Inclusion of original compositions",
          "Multi-style performance and extended improvisation",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced study",
        items: [
          "Analytical work",
          "Teaching methodology and curriculum design",
          "Music aesthetics and philosophy",
          "Optional research project or thesis-style submission",
        ],
      },
    ],
    questionBank: {
      title: "Sample theory question bank – Level 6",
      sections: [
        {
          label: "Section A",
          items: [
            "Discuss aesthetics in music.",
            "Explain advanced teaching methodologies.",
          ],
        },
        {
          label: "Section B",
          items: [
            "Curriculum design principles.",
            "Comparative analysis of styles.",
          ],
        },
        {
          label: "Section C",
          items: [
            "Research-based essay OR portfolio defense.",
          ],
        },
      ],
    },
  },
};
